"use client";

import * as React from "react";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@beweegbaas/ui/components/ui/input-group";
import { cn } from "@beweegbaas/ui/lib/utils";
import { IconSearch, IconX } from "@tabler/icons-react";

export interface SearchFieldProps extends Omit<
  React.ComponentProps<"input">,
  "value" | "onChange" | "defaultValue"
> {
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  className?: string;
  showClearButton?: boolean;
}

function SearchField({
  value,
  defaultValue,
  onChange,
  onValueChange,
  className,
  showClearButton = true,
  placeholder = "Zoeken...",
  ...props
}: SearchFieldProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const isEmpty = !currentValue || currentValue.length === 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(e);
    onValueChange?.(newValue);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isControlled) {
      setInternalValue("");
    }
    const syntheticEvent = {
      target: { value: "" },
      currentTarget: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(syntheticEvent);
    onValueChange?.("");
  };

  return (
    <InputGroup className={cn("group", className)}>
      <InputGroupInput
        value={currentValue}
        onChange={handleChange}
        placeholder={placeholder}
        className="[&::-webkit-search-cancel-button]:hidden"
        {...props}
      />
      <InputGroupAddon align="inline-start">
        <IconSearch
          aria-hidden
          className="text-muted-foreground size-4
            group-data-[disabled=true]/input-group:opacity-50"
        />
      </InputGroupAddon>
      {showClearButton && !isEmpty && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <IconX aria-hidden className="size-4" />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}

export { SearchField };
